import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, ClockIcon } from '@heroicons/react/24/outline';
import api from '../../api/client';

// Staff self-view: last 30 days of my own clock in / out history, plus
// any late-arrival / no-show rows tied to me. Read-only — corrections
// go through the director on the staff's Time & Events tab.

const EVENT_META = {
  clock_in:               { label: 'Clocked in',            cls: 'bg-green-100 text-green-700' },
  clock_out:              { label: 'Clocked out',           cls: 'bg-gray-100 text-gray-700' },
  late_arrival:           { label: '🟡 Late arrival',       cls: 'bg-amber-100 text-amber-700' },
  late_return_from_break: { label: '🟡 Long lunch',         cls: 'bg-amber-100 text-amber-700' },
  no_show:                { label: '🔴 No show',            cls: 'bg-red-100 text-red-700' },
};

// Clock times are true UTC instants — render in Eastern (the center's zone),
// not the viewer's browser timezone.
const ET = 'America/New_York';

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: ET,
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function dayKey(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { timeZone: ET, weekday: 'long', month: 'short', day: 'numeric' });
}

// "29h 05m", or "0h" when empty.
function fmtHm(h, m) {
  if (!h && !m) return '0h';
  return `${h}h${m ? ` ${String(m).padStart(2, '0')}m` : ''}`;
}

// "Jul 28 – Aug 3"
function weekRangeLabel(startIso, endIso) {
  const opt = { month: 'short', day: 'numeric' };
  const s = new Date(`${startIso}T00:00:00`).toLocaleDateString('en-US', opt);
  const e = new Date(`${endIso}T00:00:00`).toLocaleDateString('en-US', opt);
  return `${s} – ${e}`;
}

export default function MyTimePage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [hours, setHours] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/me/time-history?days=${days}`)
      .then(({ data }) => { if (!cancelled) setEvents(data.events || []); })
      .catch(() => toast.error('Failed to load your time history'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  // Worked-hours totals. Refresh on a light interval so the current week
  // ticks up while the staff member is clocked in.
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get('/me/hours?weeks=8')
      .then(({ data }) => { if (!cancelled) setHours(data); })
      .catch(() => { /* non-fatal; the event list still renders */ });
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const current = hours?.current_week;
  const priorWeeks = (hours?.weeks || []).slice(1);

  // Group events by ET calendar date for readable rows.
  const grouped = events.reduce((acc, e) => {
    const k = dayKey(e.occurred_at);
    if (!acc[k]) acc[k] = [];
    acc[k].push(e);
    return acc;
  }, {});
  const orderedDays = Object.keys(grouped);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto">
        <Link to="/my" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeftIcon className="w-4 h-4" /> Back
        </Link>
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ClockIcon className="w-6 h-6" /> My Time
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Your clock in / out history. Something wrong? Ask your director — they can fix it from their side.
            </p>
          </div>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="text-sm rounded-lg border border-gray-200 px-3 py-2 bg-white">
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>

        {/* This week (live) + reviewable weekly totals */}
        {current && (
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden mb-6">
            <div className="px-5 py-4 bg-orange-50 border-b border-orange-100 flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-orange-700">
                  This week · {weekRangeLabel(current.week_start, current.week_end)}
                </div>
                <div className="text-2xl font-bold text-gray-900">{fmtHm(current.h, current.m)}</div>
              </div>
              {current.has_open && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-100 px-2.5 py-1 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Clocked in
                </span>
              )}
            </div>
            {priorWeeks.length > 0 && (
              <ul className="divide-y divide-gray-50">
                {priorWeeks.map((w) => (
                  <li key={w.week_start} className="px-5 py-3 flex items-center justify-between">
                    <span className="text-sm text-gray-600">{weekRangeLabel(w.week_start, w.week_end)}</span>
                    <span className="flex items-center gap-2">
                      {w.has_missing && (
                        <span className="text-[11px] text-amber-600" title="A shift is missing a clock-out — ask your director to correct it">
                          missing punch
                        </span>
                      )}
                      <span className="text-sm font-semibold text-gray-900 tabular-nums">{fmtHm(w.h, w.m)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div className="px-5 py-2 bg-gray-50 text-[11px] text-gray-400">
              Hours are your actual clocked time (Mon–Sun). The current week updates as you clock in and out.
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
          </div>
        ) : orderedDays.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center">
            <ClockIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-600">No clock activity in the last {days} days.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orderedDays.map(day => (
              <div key={day} className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-600 uppercase">
                  {day}
                </div>
                <ul className="divide-y divide-gray-50">
                  {grouped[day].map(ev => {
                    const meta = EVENT_META[ev.event_type] || { label: ev.event_type, cls: 'bg-gray-100 text-gray-700' };
                    return (
                      <li key={ev.id} className="px-4 py-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                          <span className="text-sm text-gray-700 font-mono">
                            {new Date(ev.occurred_at).toLocaleTimeString('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit' })}
                          </span>
                          {ev.minutes_late != null && ev.event_type !== 'clock_in' && ev.event_type !== 'clock_out' && (
                            <span className="text-xs text-gray-500">{ev.minutes_late} min late</span>
                          )}
                          {ev.notes && (
                            <span className="text-xs text-gray-400 truncate hidden md:inline">— {ev.notes}</span>
                          )}
                        </div>
                        {ev.source && (
                          <span className="text-[10px] text-gray-400 uppercase flex-shrink-0">{ev.source.replace(/_/g, ' ')}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
